import { ArrowDown, ArrowRight } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { formatMetric } from '@/lib/format';
import { cn } from '@/lib/utils';

/*
 * Horizontal LP funnel: N stages with a pass-through % connector between each.
 * Stacks vertically on small screens (arrow flips down). Bars are proportional
 * to the largest stage so the drop shape is readable at a glance.
 */

export interface FunnelStageItem {
  key: string;
  label: ReactNode;
  value: number | null;
  /** Tailwind bg-* token class for the proportional bar (design tokens only). */
  barClass: string;
}

interface FunnelStagesProps {
  stages: FunnelStageItem[];
  /**
   * Optional connector overrides: index i = the % shown between stage i and
   * i+1. A number renders as-is, null forces the em dash (e.g. per-LP where
   * clicks are cross-site), undefined falls back to value÷previous.
   */
  connectors?: (number | null | undefined)[];
}

function passPct(prev: number | null, next: number | null): number | null {
  if (prev == null || next == null || prev <= 0) return null;
  return (next / prev) * 100;
}

export function FunnelStages({ stages, connectors }: FunnelStagesProps) {
  const max = stages.reduce((mx, s) => Math.max(mx, s.value ?? 0), 0);
  return (
    <div className="flex flex-col items-stretch gap-2 md:flex-row md:items-stretch md:gap-3">
      {stages.map((s, i) => {
        const prev = i > 0 ? stages[i - 1] : undefined;
        const override = i > 0 ? connectors?.[i - 1] : undefined;
        const pct =
          i === 0
            ? null
            : override !== undefined
              ? override
              : passPct(prev?.value ?? null, s.value);
        return (
          <Fragment key={s.key}>
            {i > 0 ? <Connector pct={pct} /> : null}
            <Stage stage={s} max={max} />
          </Fragment>
        );
      })}
    </div>
  );
}

function Connector({ pct }: { pct: number | null }) {
  return (
    <div className="flex shrink-0 items-center justify-center gap-1 self-center text-fg-subtle">
      <ArrowDown className="size-3.5 md:hidden" aria-hidden />
      <ArrowRight className="hidden size-3.5 md:block" aria-hidden />
      <span className="text-micro text-fg-muted tabular">
        {pct == null ? '—' : formatMetric(pct, 'pct')}
      </span>
    </div>
  );
}

function Stage({ stage, max }: { stage: FunnelStageItem; max: number }) {
  const widthPct =
    stage.value != null && stage.value > 0 && max > 0 ? Math.max(2, (stage.value / max) * 100) : 0;
  return (
    <div className="min-w-0 flex-1 rounded-lg border border-border bg-surface-2 p-3">
      <div className="text-label text-fg-muted">{stage.label}</div>
      <div className="mt-1 text-title text-fg tabular">{formatMetric(stage.value, 'int')}</div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-3">
        <div
          className={cn('h-full rounded-full', stage.barClass)}
          style={{ width: `${widthPct}%` }}
        />
      </div>
    </div>
  );
}
